import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Eye, Key, UserCheck, UserX, Mail, Calendar, Shield } from 'lucide-react';
import { api, usersApi, institutionsApi } from '../../services/api';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { useAuthStore } from '../../store/authStore';

const UsersPage = () => {
  const { user: currentUser } = useAuthStore();
  const isSupervisor = currentUser?.role === 'supervisor';
  const [users, setUsers] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const { toast } = useToast();

  // Role labels
  const roleLabels = {
    super_admin: 'Süper Admin',
    admin: 'Admin',
    supervisor: 'Süpervizör',
    team_leader: 'Ekip Lideri',
    operator: 'Operatör',
    paramedic: 'Paramedik',
    surucu: 'Sürücü',
    saha_personeli: 'Saha Personeli',
    muhasebe: 'Muhasebe',
    ik: 'İK',
    lojistik: 'Lojistik',
    kurumsal_pazarlama: 'Kurumsal Pazarlama',
    destek_personeli: 'Destek Personeli',
    external_institution: 'Dış Kurum',
    external_parent_institution: 'Çatı Kurum',
    external_presidency: '112 Başkanlıkları Dış Kullanıcı',
    external_hospital: '112 Hastane Dış Kullanıcı',
    mobile_user: 'Mobil Kullanıcı'
  };

  // Role colors - modern gradient colors
  const roleColors = {
    super_admin: 'bg-gradient-to-r from-amber-500 to-yellow-400 text-black',
    admin: 'bg-gradient-to-r from-red-500 to-pink-500 text-white',
    supervisor: 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white',
    team_leader: 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white',
    operator: 'bg-gradient-to-r from-green-500 to-emerald-500 text-white',
    paramedic: 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white',
    external_institution: 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white',
    external_parent_institution: 'bg-gradient-to-r from-violet-600 to-purple-700 text-white',
    external_presidency: 'bg-gradient-to-r from-cyan-600 to-teal-700 text-white',
    external_hospital: 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white',
    mobile_user: 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedRole && { role: selectedRole }),
        ...(selectedInstitution && { institution_id: selectedInstitution })
      };

      const response = await usersApi.getAll(params);
      
      if (response.data.success) {
        setUsers(response.data.data.users);
        setPagination(response.data.data.pagination);
      } else {
        throw new Error(response.data.message || 'Kullanıcılar yüklenemedi');
      }
    } catch (error) {
      console.error('Users fetch error:', error);
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Kullanıcılar yüklenirken hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutions = async () => {
    try {
      const response = await institutionsApi.getAll();
      if (response.data.success) {
        setInstitutions(response.data.data || response.data);
      }
    } catch (error) {
      console.error('Institutions fetch error:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchInstitutions();
  }, [pagination.page, searchTerm, selectedRole, selectedInstitution]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleRoleFilter = (role) => {
    setSelectedRole(role);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleInstitutionFilter = (institutionId) => {
    setSelectedInstitution(institutionId);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await usersApi.delete(userId);
      if (response.data.success) {
        toast({
          title: 'Başarılı',
          description: 'Kullanıcı başarıyla silindi',
        });
        fetchUsers();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Delete user error:', error);
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Kullanıcı silinirken hata oluştu',
        variant: 'destructive',
      });
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const response = await usersApi.update(userId, { is_active: !currentStatus });
      if (response.data.success) {
        toast({
          title: 'Başarılı',
          description: `Kullanıcı ${!currentStatus ? 'etkinleştirildi' : 'devre dışı bırakıldı'}`,
        });
        fetchUsers();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Toggle user status error:', error);
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Kullanıcı durumu değiştirilirken hata oluştu',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
      <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                Kullanıcı Yönetimi
              </h1>
              <p className="text-slate-600 mt-2">Sistem kullanıcılarını yönetin ve düzenleyin</p>
            </div>
            {!isSupervisor && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 px-6 py-3 rounded-xl"
              >
                <Plus className="h-5 w-5 mr-2" />
                Yeni Kullanıcı
              </Button>
            )}
          </div>

          {/* Modern Filters */}
          <Card className="p-6 shadow-lg border-0 bg-white/70 backdrop-blur-sm">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
                  <Input
                    type="text"
                    placeholder="Kullanıcı ara (ad, soyad, email)..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="pl-12 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl text-slate-700"
                  />
                </div>
              </div>
              {currentUser?.role !== 'external_institution' && (
                <>
                  <div className="lg:w-64">
                    <select
                      value={selectedRole}
                      onChange={(e) => handleRoleFilter(e.target.value)}
                      className="h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl w-full px-4 bg-white text-slate-700"
                    >
                      <option value="">Tüm Roller</option>
                      {Object.entries(roleLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="lg:w-64">
                    <select
                      value={selectedInstitution}
                      onChange={(e) => handleInstitutionFilter(e.target.value)}
                      className="h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl w-full px-4 bg-white text-slate-700"
                    >
                      <option value="">Tüm Kurumlar</option>
                      {institutions.map((institution) => (
                        <option key={institution.id} value={institution.id}>
                          {institution.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Toplam Kullanıcı</p>
                <p className="text-2xl font-bold">{pagination.total}</p>
              </div>
              <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center">
                <UserCheck className="h-6 w-6" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Aktif Kullanıcı</p>
                <p className="text-2xl font-bold">{users.filter(u => u.is_active).length}</p>
              </div>
              <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center">
                <UserCheck className="h-6 w-6" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Admin Sayısı</p>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'admin').length}</p>
              </div>
              <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-orange-500 to-red-500 text-white border-0 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Pasif Kullanıcı</p>
                <p className="text-2xl font-bold">{users.filter(u => !u.is_active).length}</p>
              </div>
              <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center">
                <UserX className="h-6 w-6" />
              </div>
            </div>
          </Card>
        </div>

        {/* Users Grid - Modern Card Layout */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-slate-600 text-lg">Kullanıcılar yükleniyor...</p>
            </div>
          </div>
        ) : users.length === 0 ? (
          <Card className="p-20 text-center bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <div className="text-slate-400 mb-4">
              <UserCheck className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Kullanıcı bulunamadı</h3>
            <p className="text-slate-500">Arama kriterlerinizi değiştirmeyi deneyin</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {users.map((user) => (
              <Card key={user.id} className="group bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] overflow-hidden">
                <div className="p-6">
                  {/* User Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <div className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-lg">
                            {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                          </span>
                        </div>
                        <div className={`absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-white ${user.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 text-lg group-hover:text-blue-600 transition-colors">
                          {user.first_name} {user.last_name}
                        </h3>
                        <Badge className={`${roleColors[user.role] || 'bg-gray-500'} px-3 py-1 text-xs font-medium rounded-full`}>
                          {roleLabels[user.role] || user.role || 'Bilinmeyen'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* User Details */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-slate-600">
                      <Mail className="h-4 w-4 mr-3 text-slate-400" />
                      <span className="text-sm">{user.email}</span>
                    </div>
                    <div className="flex items-center text-slate-600">
                      <div className="h-4 w-4 mr-3 text-slate-400 flex items-center justify-center">
                        🏢
                      </div>
                      <span className="text-sm">{user.institution_name || 'Atanmamış'}</span>
                    </div>
                    <div className="flex items-center text-slate-600">
                      <Calendar className="h-4 w-4 mr-3 text-slate-400" />
                      <span className="text-sm">{new Date(user.created_at).toLocaleDateString('tr-TR')}</span>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      <Badge 
                        className={`px-3 py-1 rounded-full text-xs font-medium ${user.is_active 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'}`}
                      >
                        {user.is_active ? '✓ Aktif' : '✗ Pasif'}
                      </Badge>
                      {!isSupervisor && (
                        <button
                          onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                          className={`p-2 rounded-lg transition-colors ${user.is_active 
                            ? 'text-red-500 hover:bg-red-50' 
                            : 'text-green-500 hover:bg-green-50'}`}
                          title={user.is_active ? 'Devre dışı bırak' : 'Etkinleştir'}
                        >
                          {user.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2">
                    {!isSupervisor && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowEditModal(true);
                        }}
                        className="flex-1 border-slate-200 hover:border-blue-500 hover:text-blue-600 transition-colors"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Düzenle
                      </Button>
                    )}
                    {(!isSupervisor || !['admin', 'super_admin'].includes(user.role)) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowPasswordModal(true);
                        }}
                        className={`${isSupervisor ? 'flex-1' : ''} border-slate-200 hover:border-purple-500 hover:text-purple-600 transition-colors`}
                        title="Şifre Değiştir"
                      >
                        <Key className="h-4 w-4" />
                        {isSupervisor && <span className="ml-2">Şifre Değiştir</span>}
                      </Button>
                    )}
                    {!isSupervisor && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteUser(user.id)}
                        className="border-slate-200 hover:border-red-500 hover:text-red-600 transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Modern Pagination */}
        {pagination.pages > 1 && (
          <Card className="mt-8 p-6 bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="text-slate-600">
                <span className="font-medium text-slate-900">{pagination.total}</span> kullanıcıdan 
                <span className="font-medium text-slate-900"> {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} </span>
                arası gösteriliyor
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="border-slate-200 hover:border-blue-500 hover:text-blue-600"
                >
                  Önceki
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = i + Math.max(1, pagination.page - 2);
                    return (
                      <Button
                        key={page}
                        variant={page === pagination.page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page }))}
                        className={page === pagination.page 
                          ? "bg-blue-600 hover:bg-blue-700 text-white" 
                          : "border-slate-200 hover:border-blue-500 hover:text-blue-600"}
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className="border-slate-200 hover:border-blue-500 hover:text-blue-600"
                >
                  Sonraki
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal 
          institutions={institutions}
          currentUser={currentUser}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchUsers();
          }}
        />
      )}

      {showEditModal && selectedUser && (
        <EditUserModal 
          user={selectedUser}
          institutions={institutions}
          currentUser={currentUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedUser(null);
            fetchUsers();
          }}
        />
      )}

      {showPasswordModal && selectedUser && (
        <ChangePasswordModal 
          user={selectedUser}
          onClose={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
};

// Modern Create User Modal Component
const CreateUserModal = ({ institutions, currentUser, onClose, onSuccess }) => {
  const isExternalInstitution = currentUser?.role === 'external_institution';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: isExternalInstitution ? 'mobile_user' : 'operator',
    institution_id: isExternalInstitution ? currentUser?.institution_id?.toString() || '' : '',
    hospital_id: '',
    phone: '',
    tc_no: ''
  });
  const [loading, setLoading] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const { toast } = useToast();

  // Hastaneleri yükle
  React.useEffect(() => {
    api.get('/hospitals').then(res => setHospitals(res.data?.data || [])).catch(() => {});
  }, []);

  const allRoleLabels = {
    super_admin: 'Süper Admin',
    admin: 'Admin',
    supervisor: 'Süpervizör',
    team_leader: 'Ekip Lideri',
    operator: 'Operatör',
    paramedic: 'Paramedik',
    surucu: 'Sürücü',
    saha_personeli: 'Saha Personeli',
    muhasebe: 'Muhasebe',
    ik: 'İK',
    lojistik: 'Lojistik',
    kurumsal_pazarlama: 'Kurumsal Pazarlama',
    destek_personeli: 'Destek Personeli',
    external_institution: 'Dış Kurum',
    external_parent_institution: 'Çatı Kurum',
    external_presidency: '112 Başkanlıkları Dış Kullanıcı',
    external_hospital: '112 Hastane Dış Kullanıcı',
    mobile_user: 'Mobil Kullanıcı'
  };

  // Dış kurum kullanıcıları sadece mobil kullanıcı oluşturabilir
  // Sadece super_admin, super_admin rolü atayabilir
  const roleLabels = isExternalInstitution 
    ? { mobile_user: 'Mobil Kullanıcı' }
    : currentUser?.role === 'super_admin' 
      ? allRoleLabels 
      : Object.fromEntries(Object.entries(allRoleLabels).filter(([key]) => key !== 'super_admin'));

  // Dış kurum kullanıcıları sadece kendi kurumlarını görebilir
  const filteredInstitutions = isExternalInstitution 
    ? institutions.filter(inst => inst.id === currentUser?.institution_id)
    : institutions;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await usersApi.create({
        ...formData,
        institution_id: formData.institution_id ? parseInt(formData.institution_id) : null,
        hospital_id: formData.hospital_id ? parseInt(formData.hospital_id) : null,
      });

      if (response.data.success) {
        toast({
          title: 'Başarılı',
          description: 'Kullanıcı başarıyla oluşturuldu',
        });
        onSuccess();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Create user error:', error);
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Kullanıcı oluşturulurken hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="text-center mb-8">
            <div className="h-16 w-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Yeni Kullanıcı Oluştur</h2>
            <p className="text-slate-600 mt-2">Sisteme yeni bir kullanıcı ekleyin</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">Email Adresi</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                placeholder="kullanici@emtambulans.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Şifre</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                placeholder="En az 6 karakter"
                minLength={6}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-slate-700 font-medium">Ad</Label>
                <Input
                  id="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-slate-700 font-medium">Soyad</Label>
                <Input
                  id="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="text-slate-700 font-medium">Telefon</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Auto-format: ensure +90 prefix
                    if (value && !value.startsWith('+90')) {
                      if (value.startsWith('0')) {
                        value = '+90' + value.substring(1);
                      } else if (!value.startsWith('+')) {
                        value = '+90' + value;
                      }
                    }
                    // Remove non-digit characters except +
                    value = value.replace(/[^\d+]/g, '');
                    // Limit to +90 + 10 digits
                    if (value.startsWith('+90') && value.length > 13) {
                      value = value.substring(0, 13);
                    }
                    setFormData(prev => ({ ...prev, phone: value }));
                  }}
                  placeholder="+905551234567"
                  className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                />
                <p className="text-xs text-slate-500 mt-1">Format: +90XXXXXXXXXX</p>
              </div>
              <div>
                <Label htmlFor="tc_no" className="text-slate-700 font-medium">TC Kimlik No</Label>
                <Input
                  id="tc_no"
                  type="text"
                  value={formData.tc_no}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, ''); // Only digits
                    if (value.length > 11) value = value.substring(0, 11);
                    setFormData(prev => ({ ...prev, tc_no: value }));
                  }}
                  placeholder="12345678901"
                  maxLength={11}
                  className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                />
                <p className="text-xs text-slate-500 mt-1">11 haneli TC Kimlik No</p>
              </div>
            </div>

            <div>
              <Label htmlFor="create_role" className="text-slate-700 font-medium">Kullanıcı Rolü</Label>
              <select
                id="create_role"
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg w-full px-4 bg-white text-slate-700"
                disabled={isExternalInstitution}
              >
                {Object.entries(roleLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="create_institution" className="text-slate-700 font-medium">Kurum</Label>
              <select
                id="create_institution"
                value={formData.institution_id}
                onChange={(e) => setFormData(prev => ({ ...prev, institution_id: e.target.value }))}
                className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg w-full px-4 bg-white text-slate-700"
                disabled={isExternalInstitution}
              >
                {!isExternalInstitution && <option value="">Kurum Seçin</option>}
                {filteredInstitutions.map((institution) => (
                  <option key={institution.id} value={institution.id}>
                    {institution.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Hastane seçimi — sadece external_hospital rolü için */}
            {formData.role === 'external_hospital' && (
              <div>
                <Label htmlFor="create_hospital" className="text-slate-700 font-medium">Bağlı Hastane <span className="text-red-500">*</span></Label>
                <select
                  id="create_hospital"
                  value={formData.hospital_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, hospital_id: e.target.value }))}
                  className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg w-full px-4 bg-white text-slate-700"
                >
                  <option value="">Hastane Seçin</option>
                  {hospitals.map((h) => (
                    <option key={h.id} value={h.id}>{h.name}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">112 Hastane Dış Kullanıcısı için bağlı olduğu hastaneyi seçin</p>
              </div>
            )}

            <div className="flex space-x-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1 h-12 border-slate-200 hover:border-slate-300 rounded-lg"
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg shadow-lg"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Oluşturuluyor...
                  </div>
                ) : 'Kullanıcı Oluştur'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Modern Edit User Modal Component  
const EditUserModal = ({ user, institutions, currentUser, onClose, onSuccess }) => {
  const isExternalInstitution = currentUser?.role === 'external_institution';
  
  const [formData, setFormData] = useState({
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    role: user.role,
    institution_id: user.institution_id?.toString() || '',
    hospital_id: user.hospital_id?.toString() || '',
    phone: user.phone || '',
    tc_no: user.tc_no || ''
  });
  const [loading, setLoading] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const { toast } = useToast();

  // Hastaneleri yükle
  React.useEffect(() => {
    api.get('/hospitals').then(res => setHospitals(res.data?.data || [])).catch(() => {});
  }, []);

  const allRoleLabels = {
    super_admin: 'Süper Admin',
    admin: 'Admin',
    supervisor: 'Süpervizör',
    team_leader: 'Ekip Lideri',
    operator: 'Operatör',
    paramedic: 'Paramedik',
    surucu: 'Sürücü',
    saha_personeli: 'Saha Personeli',
    muhasebe: 'Muhasebe',
    ik: 'İK',
    lojistik: 'Lojistik',
    kurumsal_pazarlama: 'Kurumsal Pazarlama',
    destek_personeli: 'Destek Personeli',
    external_institution: 'Dış Kurum',
    external_parent_institution: 'Çatı Kurum',
    external_presidency: '112 Başkanlıkları Dış Kullanıcı',
    external_hospital: '112 Hastane Dış Kullanıcı',
    mobile_user: 'Mobil Kullanıcı'
  };

  // Dış kurum kullanıcıları sadece mobil kullanıcı düzenleyebilir
  // Sadece super_admin, super_admin rolü atayabilir
  const roleLabels = isExternalInstitution 
    ? { mobile_user: 'Mobil Kullanıcı' }
    : currentUser?.role === 'super_admin' 
      ? allRoleLabels 
      : Object.fromEntries(Object.entries(allRoleLabels).filter(([key]) => key !== 'super_admin'));

  // Dış kurum kullanıcıları sadece kendi kurumlarını görebilir
  const filteredInstitutions = isExternalInstitution 
    ? institutions.filter(inst => inst.id === currentUser?.institution_id)
    : institutions;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await usersApi.update(user.id, {
        ...formData,
        institution_id: formData.institution_id ? parseInt(formData.institution_id) : null,
        hospital_id: formData.hospital_id ? parseInt(formData.hospital_id) : null,
      });

      if (response.data.success) {
        toast({
          title: 'Başarılı',
          description: 'Kullanıcı başarıyla güncellendi',
        });
        onSuccess();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Update user error:', error);
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Kullanıcı güncellenirken hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="text-center mb-8">
            <div className="h-16 w-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Edit className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Kullanıcı Düzenle</h2>
            <p className="text-slate-600 mt-2">{user.first_name} {user.last_name} kullanıcısını düzenleyin</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">Email Adresi</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-slate-700 font-medium">Ad</Label>
                <Input
                  id="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-slate-700 font-medium">Soyad</Label>
                <Input
                  id="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_phone" className="text-slate-700 font-medium">Telefon</Label>
                <Input
                  id="edit_phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Auto-format: ensure +90 prefix
                    if (value && !value.startsWith('+90')) {
                      if (value.startsWith('0')) {
                        value = '+90' + value.substring(1);
                      } else if (!value.startsWith('+')) {
                        value = '+90' + value;
                      }
                    }
                    // Remove non-digit characters except +
                    value = value.replace(/[^\d+]/g, '');
                    // Limit to +90 + 10 digits
                    if (value.startsWith('+90') && value.length > 13) {
                      value = value.substring(0, 13);
                    }
                    setFormData(prev => ({ ...prev, phone: value }));
                  }}
                  placeholder="+905551234567"
                  className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                />
                <p className="text-xs text-slate-500 mt-1">Format: +90XXXXXXXXXX</p>
              </div>
              <div>
                <Label htmlFor="edit_tc_no" className="text-slate-700 font-medium">TC Kimlik No</Label>
                <Input
                  id="edit_tc_no"
                  type="text"
                  value={formData.tc_no}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, ''); // Only digits
                    if (value.length > 11) value = value.substring(0, 11);
                    setFormData(prev => ({ ...prev, tc_no: value }));
                  }}
                  placeholder="12345678901"
                  maxLength={11}
                  className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                />
                <p className="text-xs text-slate-500 mt-1">11 haneli TC Kimlik No</p>
              </div>
            </div>

            <div>
              <Label htmlFor="edit_role" className="text-slate-700 font-medium">Kullanıcı Rolü</Label>
              <select
                id="edit_role"
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg w-full px-4 bg-white text-slate-700"
                disabled={isExternalInstitution}
              >
                {Object.entries(roleLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="edit_institution" className="text-slate-700 font-medium">Kurum</Label>
              <select
                id="edit_institution"
                value={formData.institution_id}
                onChange={(e) => setFormData(prev => ({ ...prev, institution_id: e.target.value }))}
                className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg w-full px-4 bg-white text-slate-700"
                disabled={isExternalInstitution}
              >
                {!isExternalInstitution && <option value="">Kurum Seçin</option>}
                {filteredInstitutions.map((institution) => (
                  <option key={institution.id} value={institution.id}>
                    {institution.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Hastane seçimi — sadece external_hospital rolü için */}
            {formData.role === 'external_hospital' && (
              <div>
                <Label htmlFor="edit_hospital" className="text-slate-700 font-medium">Bağlı Hastane <span className="text-red-500">*</span></Label>
                <select
                  id="edit_hospital"
                  value={formData.hospital_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, hospital_id: e.target.value }))}
                  className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg w-full px-4 bg-white text-slate-700"
                >
                  <option value="">Hastane Seçin</option>
                  {hospitals.map((h) => (
                    <option key={h.id} value={h.id}>{h.name}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">112 Hastane Dış Kullanıcısı için bağlı olduğu hastaneyi seçin</p>
              </div>
            )}

            <div className="flex space-x-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1 h-12 border-slate-200 hover:border-slate-300 rounded-lg"
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg shadow-lg"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Güncelleniyor...
                  </div>
                ) : 'Değişiklikleri Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Modern Change Password Modal Component
const ChangePasswordModal = ({ user, onClose, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await usersApi.changePassword(user.id, password);

      if (response.data.success) {
        toast({
          title: 'Başarılı',
          description: 'Şifre başarıyla değiştirildi',
        });
        onSuccess();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Change password error:', error);
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Şifre değiştirilirken hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-8">
          <div className="text-center mb-8">
            <div className="h-16 w-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Şifre Değiştir</h2>
            <p className="text-slate-600 mt-2">{user.first_name} {user.last_name} için yeni şifre</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Yeni Şifre</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                placeholder="En az 6 karakter"
                minLength={6}
                required
              />
            </div>

            <div className="flex space-x-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1 h-12 border-slate-200 hover:border-slate-300 rounded-lg"
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-lg shadow-lg"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Değiştiriliyor...
                  </div>
                ) : 'Şifre Değiştir'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UsersPage; 